import useChains from './useChains';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getBalance, GetBalanceReturnType } from '@wagmi/core';
import { formatUnits, Hex } from 'viem';
import useTomoUserInfo from './useTomoUserInfo';
import useConfig from './useConfig';
import { btcDecimals, mockBtcEvmChainId } from '../config/btc';
import { getSolBalance, mockSolEvmChainId, solDecimals } from '../config/sol';
import { Web3Type } from '../config/type';
import useBTCBalance from './useBTCBalance';
import { useSolBalance } from './useSolBalance';
import { BTCNetworkType } from '../api/type';
import { mockTonChainId, getTonBalance, tonDecimals } from '../config/ton';

/**
 * Fetches balance information using the specified chain ID, token, and network type.
 *
 * @param {number | undefined} chainId - The ID of the blockchain. If `chainId` is 0, `netWorkType` is a required parameter.
 * @param {string} [token] - The identifier of the token.
 * @param {BTCNetworkType} [netWorkType] - The network type, which can be "MAINNET", "TESTNET", or "SIGNET".
 */
const useBalance = ({
  chainId,
  token,
  netWorkType,
}: {
  chainId: number | undefined;
  token?: string;
  netWorkType?: BTCNetworkType;
}) => {
  const { chains } = useChains();
  const { solAddress, evmAddress, tonAddress } = useTomoUserInfo();
  const btcBalanceQuery = useBTCBalance(netWorkType);
  const solBalanceQuery = useSolBalance();
  const { config } = useConfig();

  const chain = useMemo(() => {
    if (chainId === mockBtcEvmChainId) {
      return chains.find(item => item.type === Web3Type.BTC);
    }
    if (chainId === mockSolEvmChainId) {
      return chains.find(item => item.type === Web3Type.SOL);
    }
    if (chainId === mockTonChainId) {
      return chains.find(item => item.type === Web3Type.TON);
    }
    return chains.find(item => {
      return item.chain?.id === chainId;
    });
  }, [chainId, chains]);

  const balance = useQuery({
    queryKey: ['useAllChainBalance', chainId, token, chain],
    queryFn: async () => {
      if (chain?.type === Web3Type.BTC) {
        const value =
          btcBalanceQuery.data?.reduce((acc: number, item) => {
            return acc + item.value;
          }, 0) ?? 0;

        const balance = {
          decimals: btcDecimals,
          formatted: formatUnits(BigInt(value), btcDecimals),
          symbol: 'BTC',
          value: BigInt(value),
        };

        return balance;
      } else if (chain?.type === Web3Type.SOL) {
        if (token) {
          const balanceSolSPLToken = (await getSolBalance({
            address: solAddress,
            token: token,
          })) as {
            amount: bigint;
            format: any;
            decimals: any;
            token: string;
          };

          const decimals: number | undefined = balanceSolSPLToken
            ? balanceSolSPLToken.decimals
            : undefined;

          const value = balanceSolSPLToken
            ? balanceSolSPLToken.amount
            : BigInt(0);

          const balance = {
            decimals: decimals,
            formatted: decimals ? formatUnits(value, decimals) : '0',
            symbol: balanceSolSPLToken.token,
            value: value,
          };

          return balance;
        } else {
          const amount = solBalanceQuery.data;
          const value = amount ? amount : BigInt(0);
          return {
            decimals: solDecimals,
            formatted: formatUnits(value, solDecimals),
            symbol: 'SOL',
            value: value,
          };
        }
      } else if (chain?.type === Web3Type.EVM) {
        const balance = await getBalance(config, {
          address: evmAddress as Hex,
          chainId: chainId,
          token: token as Hex,
        });

        return balance;
      } else if (chain?.type === Web3Type.TON) {
        const balance = await getTonBalance(tonAddress);
        return {
          decimals: tonDecimals,
          formatted: balance.formatted,
          symbol: 'TON',
          value: balance.balance,
        };
      }

      return undefined;
    },
  });

  return balance;
};

export default useBalance;
